import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiHelper } from '../lib/api';

export const leaveRequestKeys = {
    all: ['leave-requests'],
    lists: () => [...leaveRequestKeys.all, 'list'],
    list: (orgId) => [...leaveRequestKeys.lists(), orgId],
    details: () => [...leaveRequestKeys.all, 'detail'],
    detail: (id) => [...leaveRequestKeys.details(), id],
};

// Fetch leave requests
export function useLeaveRequests(orgId, { suppressToast = false } = {}) {
    return useQuery({
        queryKey: leaveRequestKeys.list(orgId),
        queryFn: async () => {
            return apiHelper.getResource('/leave/requests/', { orgId, suppressToast });
        },
        enabled: !!orgId,
        retry: false,
    });
}

// Download leave report (month or range) as Excel.
// GET /leave/download/monthly/?year=&month=[&status=&employee_id=]
// GET /leave/download/range/?start_year=&start_month=&end_year=&end_month=[&status=&employee_id=]
export function useDownloadLeaveReport() {
    return useMutation({
        mutationFn: ({ mode, year, month, startYear, startMonth, endYear, endMonth, employeeId, status, orgId }) => {
            const params = new URLSearchParams();
            let path;
            if (mode === 'range') {
                path = '/leave/download/range/';
                params.set('start_year', startYear);
                params.set('start_month', startMonth);
                params.set('end_year', endYear);
                params.set('end_month', endMonth);
            } else {
                path = '/leave/download/monthly/';
                if (year != null) params.set('year', year);
                if (month != null) params.set('month', month);
            }
            if (employeeId) params.set('employee_id', employeeId);
            if (status) params.set('status', status);
            const filename = mode === 'range'
                ? `Leave_Report_${startYear}-${String(startMonth).padStart(2, '0')}_to_${endYear}-${String(endMonth).padStart(2, '0')}.xlsx`
                : `Leave_Report_${year}-${String(month).padStart(2, '0')}.xlsx`;
            return apiHelper.downloadResource(`${path}?${params.toString()}`, filename, { orgId });
        },
        onSuccess: () => toast.success('Leave report downloaded.'),
    });
}

// Create leave request
export function useCreateLeaveRequest() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource('/leave/requests/', data, options);
        },
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({ queryKey: leaveRequestKeys.list(orgId) });
        },
    });
}

// Update leave request (Approve/Reject)
export function useUpdateLeaveRequest() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.putResource(`/leave/requests/${id}/`, data, options); // or PATCH
        },
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({ queryKey: leaveRequestKeys.list(orgId) });
        },
    });
}

// Delete leave request
export function useDeleteLeaveRequest() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.deleteResource(`/leave/requests/${id}/`, options);
        },
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({ queryKey: leaveRequestKeys.list(orgId) });
        },
    });
}

// Confirm leave request (Manager)
export function useConfirmLeaveRequest() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId, data }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource(`/leave/requests/${id}/confirm/`, data, options);
        },
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({ queryKey: leaveRequestKeys.list(orgId) });
        },
    });
}

// http://127.0.0.1:8000/api/leave/requests/{id}/

// Approve leave request (HR)
export function useApproveLeaveRequest() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource(`/leave/requests/${id}/approve/`, {}, options);
        },
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({ queryKey: leaveRequestKeys.list(orgId) });
        },
    });
}

// Reject leave request (Manager or HR)
export function useRejectLeaveRequest() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId, data }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource(`/leave/requests/${id}/reject/`, data, options);
        },
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({ queryKey: leaveRequestKeys.list(orgId) });
        },
    });
}

// Upload attachment to existing leave request
export function useUploadLeaveAttachment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, files, orgId }) => {
            const formData = new FormData();
            files.forEach(file => formData.append('attachments', file));
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource(`/leave/requests/${id}/upload-attachment/`, formData, options);
        },
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({ queryKey: leaveRequestKeys.list(orgId) });
        },
    });
}

// Cancel leave request (Employee)
export function useCancelLeaveRequest() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, orgId }) => {
            const options = orgId ? { orgId } : {};
            return apiHelper.postResource(`/leave/requests/${id}/cancel/`, {}, options);
        },
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({ queryKey: leaveRequestKeys.list(orgId) });
        },
    });
}
